import { useEffect, useState, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import useEscapeKey from "../hooks/useEscapeKey";
import {
  Building2,
  Plus,
  Search,
  MoreVertical,
  X,
  Upload,
} from "lucide-react";
import { fetchDeals, createDeal, duplicateDeal, deleteDeal, type Deal } from "../api/client";
import { useToastContext } from "../contexts/ToastContext";
import StatusBadge from "../components/StatusBadge";
import EmptyState from "../components/EmptyState";
import LoadingSpinner from "../components/LoadingSpinner";
import { fmtDate, fmtRelativeTime } from "../utils/format";
import { friendlyError } from "../utils/errors";

export default function DealsListPage() {
  const navigate = useNavigate();
  const { addToast } = useToastContext();
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  useEscapeKey(useCallback(() => setShowCreate(false), []), showCreate);
  const [form, setForm] = useState({ name: "", address: "", asset_class: "office" });
  const [creating, setCreating] = useState(false);
  const [sortBy, setSortBy] = useState<"created" | "modified" | "name" | "name_desc" | "asset_class">("created");
  const [assetFilter, setAssetFilter] = useState<string>("all");
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const refreshDeals = useCallback(() => {
    fetchDeals()
      .then(setDeals)
      .catch((e) => setError(friendlyError(e instanceof Error ? e.message : "Failed to load deals. Is the API running?")))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    refreshDeals();
  }, [refreshDeals]);

  // Close action menu when clicking outside
  useEffect(() => {
    if (!openMenu) return;
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenu(null);
      }
    }
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [openMenu]);

  const filtered = deals
    .filter(
      (d) =>
        d.name.toLowerCase().includes(search.toLowerCase()) ||
        (d.address || "").toLowerCase().includes(search.toLowerCase())
    )
    .filter((d) => assetFilter === "all" || d.asset_class === assetFilter)
    .sort((a, b) => {
      switch (sortBy) {
        case "created":
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case "modified":
          return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
        case "name":
          return a.name.localeCompare(b.name);
        case "name_desc":
          return b.name.localeCompare(a.name);
        case "asset_class":
          return (a.asset_class || "").localeCompare(b.asset_class || "");
        default:
          return 0;
      }
    });

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) return;
    setCreating(true);
    try {
      const deal = await createDeal({
        name: form.name.trim(),
        address: form.address.trim() || undefined,
        asset_class: form.asset_class || undefined,
      });
      navigate(`/deals/${deal.id}`);
    } catch {
      addToast("Couldn't create the property. Try again in a moment.", "error");
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="max-w-[1280px] mx-auto px-8 py-10 animate-fade-in">
      {/* Hero / Welcome Section */}
      <div className="bg-white rounded-xl border border-grove-300 shadow-sm p-8 mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-3xl font-semibold text-bark-800 tracking-tight mb-2">
              Welcome back
            </h1>
            <p className="text-bark-400">
              {deals.length} {deals.length === 1 ? "property" : "properties"} under management.{" "}
              {deals.length === 0 ? "Add your first property to get started." : "Open one or add a new one."}
            </p>
          </div>
          <div className="flex items-center gap-3 relative z-10">
            <button
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-honey-600 text-white text-sm font-semibold rounded-lg hover:bg-honey-500 hover:shadow-lg transition-all duration-200 shadow-md cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              New Property
            </button>
          </div>
        </div>
      </div>

      {/* Quick action: Upload prompt */}
      {!loading && deals.length > 0 && (
        <div className="bg-forest-50 border border-forest-200 rounded-xl p-6 mb-8 flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-forest-100 border border-forest-200 flex items-center justify-center flex-shrink-0">
            <Upload className="w-5 h-5 text-forest-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-forest-800">Ready to analyze a new property?</p>
            <p className="text-sm text-forest-600 mt-0.5">Add a property, then upload your rent roll to start underwriting.</p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="px-5 py-2.5 bg-forest-700 text-white text-sm font-semibold rounded-lg hover:bg-forest-600 hover:shadow-md transition-all duration-200"
          >
            Get Started
          </button>
        </div>
      )}

      {!loading && deals.length > 0 && (
        <div className="mb-6 space-y-3">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-bark-300" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search deals by name or address..."
                className="w-full pl-11 pr-4 py-3 text-sm bg-white border border-grove-300 rounded-xl text-bark-700 placeholder:text-bark-300 transition-all"
              />
            </div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              className="px-3 py-3 text-sm bg-white border border-grove-300 rounded-xl text-bark-700"
            >
              <option value="created">Newest First</option>
              <option value="modified">Recently Modified</option>
              <option value="name">Name A-Z</option>
              <option value="name_desc">Name Z-A</option>
              <option value="asset_class">Asset Class</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            {[
              { value: "all", label: "All" },
              { value: "retail", label: "Retail" },
              { value: "office", label: "Office" },
              { value: "industrial", label: "Industrial" },
              { value: "mixed_use", label: "Mixed Use" },
            ].map((pill) => (
              <button
                key={pill.value}
                onClick={() => setAssetFilter(pill.value)}
                className={`px-4 py-1.5 text-sm rounded-full transition-colors ${
                  assetFilter === pill.value
                    ? "bg-forest-700 text-white"
                    : "bg-grove-50 border border-grove-300 text-bark-500 hover:bg-grove-100"
                }`}
              >
                {pill.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
          <p className="text-red-700 font-medium mb-2">Couldn't load your properties</p>
          <p className="text-red-500 text-sm mb-4">{error}</p>
          <button
            onClick={() => { setError(null); setLoading(true); fetchDeals().then(setDeals).catch((e) => setError(friendlyError(e instanceof Error ? e.message : "Couldn't load your properties"))).finally(() => setLoading(false)); }}
            className="px-5 py-2.5 text-sm bg-white border border-grove-300 text-bark-600 rounded-lg hover:bg-grove-50 hover:shadow-sm transition-all"
          >
            Retry
          </button>
        </div>
      ) : deals.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="No properties yet"
          description="Add your first property to start underwriting."
          action={
            <button
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-honey-600 text-white text-sm font-semibold rounded-lg hover:bg-honey-500 hover:shadow-lg transition-all duration-200 shadow-md"
            >
              <Plus className="w-4 h-4" />
              Add Property
            </button>
          }
        />
      ) : (
        <div className="bg-white rounded-xl border border-grove-300 overflow-hidden shadow-sm">
          <table className="w-full data-table">
            <thead>
              <tr>
                <th>Property</th>
                <th>Asset Class</th>
                <th>Status</th>
                <th>Modified</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((deal, i) => (
                <tr key={deal.id} className="group cursor-pointer hover:bg-grove-50 transition-colors" style={{ animationDelay: `${i * 30}ms` }} onClick={() => navigate(`/deals/${deal.id}`)}>
                  <td>
                    <Link
                      to={`/deals/${deal.id}`}
                      className="font-medium text-bark-700 hover:text-forest-700 transition-colors"
                    >
                      {deal.name}
                    </Link>
                    {deal.address && (
                      <p className="text-xs text-bark-300 mt-0.5">
                        {deal.address}
                      </p>
                    )}
                  </td>
                  <td className="capitalize text-bark-500">{deal.asset_class || "--"}</td>
                  <td>
                    <StatusBadge status={deal.status} />
                  </td>
                  <td className="text-bark-400" title={fmtDate(deal.updated_at)}>{fmtRelativeTime(deal.updated_at)}</td>
                  <td>
                    <div className="relative" ref={openMenu === deal.id ? menuRef : undefined}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenu(openMenu === deal.id ? null : deal.id);
                        }}
                        className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center justify-center w-8 h-8 rounded-lg hover:bg-grove-100"
                      >
                        <MoreVertical className="w-4 h-4 text-bark-400" />
                      </button>
                      {openMenu === deal.id && (
                        <div className="absolute right-0 top-8 z-20 bg-white border border-grove-300 rounded-lg shadow-lg py-1 w-44">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(`/deals/${deal.id}`, "_blank");
                              setOpenMenu(null);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-bark-700 hover:bg-grove-50 transition-colors"
                          >
                            Open in New Tab
                          </button>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              setOpenMenu(null);
                              try {
                                await duplicateDeal(deal.id);
                                refreshDeals();
                                addToast("Property duplicated", "success");
                              } catch {
                                addToast("Couldn't duplicate the property. Try again.", "error");
                              }
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-bark-700 hover:bg-grove-50 transition-colors"
                          >
                            Duplicate
                          </button>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              setOpenMenu(null);
                              if (!window.confirm(`Delete "${deal.name}"? This cannot be undone.`)) return;
                              try {
                                await deleteDeal(deal.id);
                                refreshDeals();
                                addToast("Property deleted", "success");
                              } catch {
                                addToast("Couldn't delete the property. Try again.", "error");
                              }
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-bark-900/40 backdrop-blur-sm" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-xl border border-grove-300 shadow-2xl w-full max-w-md mx-4 p-8 animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-xl font-semibold text-bark-800">
                New Property
              </h2>
              <button
                onClick={() => setShowCreate(false)}
                className="p-2 rounded-lg hover:bg-grove-100 transition-colors"
              >
                <X className="w-5 h-5 text-bark-400" />
              </button>
            </div>
            <form onSubmit={handleCreate} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-bark-600 mb-2">
                  Property Name
                </label>
                <input
                  autoFocus
                  required
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Riverchase Shopping Center"
                  className="w-full px-4 py-3 text-sm bg-white border border-grove-300 rounded-lg text-bark-700 placeholder:text-bark-300"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-bark-600 mb-2">
                  Address
                </label>
                <input
                  value={form.address}
                  onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                  placeholder="e.g. 100 Main St, Birmingham, AL"
                  className="w-full px-4 py-3 text-sm bg-white border border-grove-300 rounded-lg text-bark-700 placeholder:text-bark-300"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-bark-600 mb-2">
                  Asset Class
                </label>
                <select
                  value={form.asset_class}
                  onChange={(e) => setForm((f) => ({ ...f, asset_class: e.target.value }))}
                  className="w-full px-4 py-3 text-sm bg-white border border-grove-300 rounded-lg text-bark-700"
                >
                  <option value="office">Office</option>
                  <option value="retail">Retail</option>
                  <option value="industrial">Industrial</option>
                  <option value="mixed_use">Mixed Use</option>
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  className="flex-1 px-4 py-3 text-sm font-medium border border-grove-300 rounded-lg text-bark-500 hover:bg-grove-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="flex-1 px-4 py-3 text-sm font-semibold bg-honey-600 text-white rounded-lg hover:bg-honey-500 disabled:opacity-50 transition-all shadow-md hover:shadow-lg"
                >
                  {creating ? "Creating..." : "Create Property"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
